import dayjs from "../utils/dayJs";

export const formatSortCode = (value: string): string => {
  const input = value.replace(/[^a-zA-Z0-9]/g, ""); // Allow only alphanumeric characters (remove special characters)
  return input.replace(/(.{2})(?=.)/g, "$1-"); // Add a dash after every 2 characters
};

type ModulePermission = {
  module_name: string;
  permissions: string[];
};

type PermissionFlags = {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
};
/**
 * Get boolean permission flags for a specific module
 * @param modulePermissions - Full permission array from user
 * @param moduleName - Module name to check (case-insensitive)
 * @returns PermissionFlags object (all false if not found)
 */

export const capitalizeWords = (str: any) =>
  str
    ? str
        .toLowerCase()
        .split(" ")
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "";

export function getModulePermissionFlags(
  modulePermissions: ModulePermission[],
  moduleName: string,
): PermissionFlags {
  const module = modulePermissions.find(
    (mod) =>
      mod.module_name.trim().toLowerCase() === moduleName.trim().toLowerCase(),
  );

  const basePermissions: PermissionFlags = {
    create: false,
    read: false,
    update: false,
    delete: false,
  };

  if (!module) return basePermissions;

  return {
    create: module.permissions.includes("create"),
    read: module.permissions.includes("read"),
    update: module.permissions.includes("update"),
    delete: module.permissions.includes("delete"),
  };
}

// change to price format
export const formatPrice = (value: number | string) => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0";
  return `${num.toLocaleString("en-UK")}`;
};

export const formatNumberWithComma = (value: number | string): string => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0.00";

  return num?.toLocaleString("en-UK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatDate = (dateStr: string) => {
  const date = new Date(dateStr.trim());
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const formatPeriod = (periodStr: string) => {
  if (!periodStr.includes("–")) return periodStr; // fallback
  const [start, end] = periodStr.split("–");
  return `${formatDate(start)} – ${formatDate(end)}`;
};

// current date DD Monthe YY format
// utils/dateFormatter.ts

export const shortFormatDay = (
  date: Date | string | number = new Date(),
  options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  },
  locale: string = "en-GB",
): string => {
  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) return "";

  return parsedDate.toLocaleDateString(locale, options);
};

// Utility Function
export const formatVolumeUnit = (unit: string) => {
  if (!unit) return "";

  return unit
    .replace("m3", "m³")
    .replace("ft3", "ft³")
    .replace("M3", "M³")
    .replace("FT3", "FT³");
};

export const convertPermissions = (modulePermissions: any[]) => {
  const permissions: any = {};

  modulePermissions.forEach((module) => {
    permissions[module.module_name] = {
      create: module.permissions?.includes("create") || false,
      read: module.permissions?.includes("read") || false,
      update: module.permissions?.includes("update") || false,
      delete: module.permissions?.includes("delete") || false,
    };
  });

  return permissions;
};

export const PERMISSION_MODULES = {
  COMM_EMAIL: "Communication:Email",
  COMM_SMS: "Communication:Sms",
  FILE_UPLOAD: "File Upload",
  INVOICE: "Invoice",
  LEAD: "Lead",
  LEAD_ARCHIVE: "Lead: Archive",
  LEAD_COMPLETION: "Lead:Completion",
  LEAD_CONFIRMATION: "Lead:Confirmation",
  LEAD_ESTIMATE: "Lead:Estimate",
  LEAD_JOB: "Lead:Job",
  LEAD_LOG: "Lead:Log",
  LEAD_PROVISIONAL: "Lead:Provisional",
  LEAD_QUOTATION: "Lead:Quotation",
  LEAD_SURVEY: "Lead:Survey",
  NOTIFICATION: "Notification",
  OPERATIONS: "Operations",
  PAYMENT: "Payment",
  SETTING: "Setting",
  SETTING_COMMUNICATIONS: "Setting: Communications",
  SETTING_COMPANY: "Setting:Company",
  SETTING_CUSTOMER: "Setting: Customer",
  SETTING_DOCUMENT: "Setting:Document",
  SETTING_FEEDBACK: "Setting:Feedback",
  SETTING_FINANCIALS: "Setting:Financials",
  SETTING_OWNER: "Setting:Owner",
  SETTING_REFERRER: "Setting:Referrer",
  SETTING_ROLE: "Setting:Role",
  SETTING_SALES: "Setting:Sales",
  SETTING_STAFF: "Setting:Staff",
  SETTING_WORKER_VEHICLE: "Setting:Worker And Vehicle",
  SURVEY_TASKS: "Survey Tasks",
  SURVEYOR: "Surveyor",
  TASK: "Task",
  WORK_DIARY: "Work Diary",
};

export const hasPermission = (
  permissions:any,
  module:string,
  action:string="read"
)=>{
  return permissions?.[module]?.[action] || false
}

// time range
  export const mergeDateAndTime = (date: any, time: any) => {
    if (!date || !time) return "";
    const d = dayjs(date);
    const t = dayjs(time);
    if (!d.isValid() || !t.isValid()) return "";
    return d
      .hour(t.hour())
      .minute(t.minute())
      .second(0)
      .millisecond(0)
      .toISOString();
  };

  export const formatTime = (time: string | null) => {
  if (!time) return null;
  return time.slice(0, 5); // "09:30:00" -> "09:30"
};